import React, { useState } from "react";
import CompanyPicker from "./CompanyPicker";

// "Today" as a bare YYYY-MM-DD built from LOCAL calendar parts. Deliberately not
// `toISOString().slice(0,10)`, which formats in UTC and is a day ahead for US-timezone users in the
// evening — that would let the picker offer a "today" the server (started/ended must be in the past)
// would then reject.
const localTodayDate = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = `${now.getMonth() + 1}`.padStart(2, "0");
  const day = `${now.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

function EditEmployment({ employment, onConfirm, onCancel, disabled = false }) {
  const [employmentData, setEmploymentData] = useState({
    companyId: employment.companyId,
    companyName: employment.companyName,
    title: employment.title,
    from: employment.from,
    to: employment.to,
  });
  const [companyCreating, setCompanyCreating] = useState(false);

  // Confirm is blocked while a mutation is already in flight (a second click would be silently swallowed
  // by the single-flight employments hook) or while a company create is still resolving its id.
  const confirmDisabled = disabled || companyCreating;

  const handleChange = (field) => (value) => {
    setEmploymentData((prev) => ({ ...prev, [field]: value }));
  };

  // The stored value can be a full timestamp (company_jobs date columns are `timestamp without time
  // zone` and migrated rows carry real non-midnight times — e.g. 04:00:00, a V1-Eastern-midnight
  // artifact), but <input type="date"> needs a bare YYYY-MM-DD. The stored calendar date is the truth,
  // so keep it verbatim — no Date parsing / timezone conversion, which would shift the day.
  const adjustDateValue = (value) => (value ? value.slice(0, 10) : "");

  const confirmChanges = () => {
    onConfirm(employmentData);
  };

  const onCompanyChange = (company) => {
    setEmploymentData((prev) => ({
      ...prev,
      companyId: company.value,
      companyName: company.label,
    }));
  };

  // min/max keep the pickers from offering illogical dates (start in the future, end before start) that
  // the server would reject anyway. They are UX only — server validation + the ServerErrorAlert path stay
  // the enforcement backstop, so an out-of-range pre-existing stored value still renders (attributes never
  // clamp the value).
  const today = localTodayDate();

  return (
    <tr>
      <td>
        <CompanyPicker
          companyName={employmentData.companyName}
          companyId={employmentData.companyId}
          onCompanyChange={onCompanyChange}
          onCreatingChange={setCompanyCreating}
        />
      </td>
      <td>
        <input
          className="form-control"
          onChange={(e) => handleChange("title")(e.currentTarget.value)}
          value={employmentData.title}
        />
      </td>
      <td>
        <input
          className="form-control"
          type="date"
          value={adjustDateValue(employmentData.from)}
          max={adjustDateValue(employmentData.to) || today}
          onChange={(e) => handleChange("from")(e.currentTarget.value)}
        />
      </td>
      <td>
        <input
          className="form-control"
          type="date"
          value={adjustDateValue(employmentData.to)}
          min={adjustDateValue(employmentData.from) || undefined}
          max={today}
          onChange={(e) => handleChange("to")(e.currentTarget.value)}
        />
      </td>
      <td className="text-nowrap">
        <div className="d-flex">
          <button
            onClick={confirmChanges}
            type="button"
            className="btn btn-sm btn-outline-primary d-flex"
            disabled={confirmDisabled}
          >
            <i className="fas fa-check" />
          </button>
          &nbsp;
          <button
            onClick={onCancel}
            type="button"
            className="btn btn-sm btn-outline-secondary d-flex"
            disabled={disabled}
          >
            <i className="fas fa-xmark" />
          </button>
        </div>
      </td>
    </tr>
  );
}

export default EditEmployment;
